// user-preferences-normalizers.ts — Current-user suite, preference sanitizing.

import {
  CHAT_COMPOSER_MINIMUM_ROUNDS,
  SessionContentVersionRenderMode,
  SESSION_CONTROL_MINIMUM_ROUNDS,
  SESSION_FOLD_PREFERENCES_DEFAULTS,
  USER_PREFERENCES_DEFAULTS,
  type SessionFoldPreferences,
  type UserPreferences,
} from './user-preferences-model';

interface SanitizedSessionFoldPreferenceParts {
  topPanelFolded?: boolean;
  chatFolded?: boolean;
  overviewFolded?: boolean;
  contentVersionRenderMode?: SessionContentVersionRenderMode;
}

function sanitizeNonNegativeInteger(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isInteger(value) || value < 0) {
    return undefined;
  }

  return value;
}

function sanitizeBoolean(value: unknown): boolean | undefined {
  return typeof value === 'boolean' ? value : undefined;
}

function sanitizeSessionContentVersionRenderMode(
  value: unknown,
): SessionContentVersionRenderMode | undefined {
  if (value === SessionContentVersionRenderMode.Full) {
    return SessionContentVersionRenderMode.Full;
  }

  if (value === SessionContentVersionRenderMode.Accordion) {
    return SessionContentVersionRenderMode.Accordion;
  }

  return undefined;
}

function isRecordValue(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value != null && !Array.isArray(value);
}

function sanitizeSessionFoldPreferenceParts(
  value: Record<string, unknown>,
): SanitizedSessionFoldPreferenceParts {
  return {
    topPanelFolded: sanitizeBoolean(value.topPanelFolded),
    chatFolded: sanitizeBoolean(value.chatFolded),
    overviewFolded: sanitizeBoolean(value.overviewFolded),
    contentVersionRenderMode: sanitizeSessionContentVersionRenderMode(
      value.contentVersionRenderMode,
    ),
  };
}

function hasRecognizedFoldPreference(
  parts: SanitizedSessionFoldPreferenceParts,
): boolean {
  return Object.values(parts).some((preferenceValue) => preferenceValue != null);
}

function applyPreferenceDefault<T>(input: {
  value: T | undefined;
  defaultValue: T;
}): T {
  return input.value ?? input.defaultValue;
}

function applySessionFoldPreferenceDefaults(
  parts: SanitizedSessionFoldPreferenceParts,
): SessionFoldPreferences {
  return {
    topPanelFolded: applyPreferenceDefault({
      value: parts.topPanelFolded,
      defaultValue: SESSION_FOLD_PREFERENCES_DEFAULTS.topPanelFolded,
    }),
    chatFolded: applyPreferenceDefault({
      value: parts.chatFolded,
      defaultValue: SESSION_FOLD_PREFERENCES_DEFAULTS.chatFolded,
    }),
    overviewFolded: applyPreferenceDefault({
      value: parts.overviewFolded,
      defaultValue: SESSION_FOLD_PREFERENCES_DEFAULTS.overviewFolded,
    }),
    contentVersionRenderMode: applyPreferenceDefault({
      value: parts.contentVersionRenderMode,
      defaultValue: SESSION_FOLD_PREFERENCES_DEFAULTS.contentVersionRenderMode,
    }),
  };
}

function sanitizeSessionFoldPreferences(
  value: unknown,
): SessionFoldPreferences | undefined {
  if (!isRecordValue(value)) {
    return undefined;
  }

  const sanitizedParts = sanitizeSessionFoldPreferenceParts(value);
  if (!hasRecognizedFoldPreference(sanitizedParts)) {
    return undefined;
  }

  return applySessionFoldPreferenceDefaults(sanitizedParts);
}

function sanitizeSessionFoldPreferencesBySessionId(
  value: unknown,
): Record<string, SessionFoldPreferences> {
  if (!isRecordValue(value)) {
    return {};
  }

  return Object.entries(value).reduce<Record<string, SessionFoldPreferences>>(
    (preferencesBySessionId, [sessionId, sessionPreferences]) => {
      const normalizedSessionId = sessionId.trim();
      const sanitizedSessionPreferences =
        sanitizeSessionFoldPreferences(sessionPreferences);

      if (!normalizedSessionId || !sanitizedSessionPreferences) {
        return preferencesBySessionId;
      }

      return {
        ...preferencesBySessionId,
        [normalizedSessionId]: sanitizedSessionPreferences,
      };
    },
    {},
  );
}

function resolveBooleanPreference(input: {
  nextValue: unknown;
  currentValue: boolean;
}): boolean {
  return sanitizeBoolean(input.nextValue) ?? input.currentValue;
}

function resolveSessionContentVersionRenderMode(input: {
  nextValue: unknown;
  currentValue: SessionContentVersionRenderMode;
}): SessionContentVersionRenderMode {
  return (
    sanitizeSessionContentVersionRenderMode(input.nextValue) ??
    input.currentValue
  );
}

function sanitizeMinimumRounds(input: {
  value: unknown;
  minimum: number;
  defaultValue: number;
}): number {
  const sanitizedValue = sanitizeNonNegativeInteger(input.value);

  if (sanitizedValue == null || sanitizedValue < input.minimum) {
    return input.defaultValue;
  }

  return sanitizedValue;
}

export function mergeSessionFoldPreferences(input: {
  current: SessionFoldPreferences;
  next: Partial<SessionFoldPreferences>;
}): SessionFoldPreferences {
  const { current, next } = input;

  return {
    topPanelFolded: resolveBooleanPreference({
      nextValue: next.topPanelFolded,
      currentValue: current.topPanelFolded,
    }),
    chatFolded: resolveBooleanPreference({
      nextValue: next.chatFolded,
      currentValue: current.chatFolded,
    }),
    overviewFolded: resolveBooleanPreference({
      nextValue: next.overviewFolded,
      currentValue: current.overviewFolded,
    }),
    contentVersionRenderMode: resolveSessionContentVersionRenderMode({
      nextValue: next.contentVersionRenderMode,
      currentValue: current.contentVersionRenderMode,
    }),
  };
}

export function sanitizeUserPreferences(
  value: Record<string, unknown>,
): UserPreferences {
  const enterToSend = sanitizeBoolean(value.enterToSend);
  const sessionFoldPreferences = sanitizeSessionFoldPreferencesBySessionId(
    value.sessionFoldPreferences,
  );
  const lastSessionLayout =
    sanitizeSessionFoldPreferences(value.lastSessionLayout) ??
    USER_PREFERENCES_DEFAULTS.lastSessionLayout;

  return {
    chatComposerRounds: sanitizeMinimumRounds({
      value: value.chatComposerRounds,
      minimum: CHAT_COMPOSER_MINIMUM_ROUNDS,
      defaultValue: USER_PREFERENCES_DEFAULTS.chatComposerRounds,
    }),
    sessionControlRounds: sanitizeMinimumRounds({
      value: value.sessionControlRounds,
      minimum: SESSION_CONTROL_MINIMUM_ROUNDS,
      defaultValue: USER_PREFERENCES_DEFAULTS.sessionControlRounds,
    }),
    enterToSend: enterToSend ?? USER_PREFERENCES_DEFAULTS.enterToSend,
    lastSessionLayout,
    sessionFoldPreferences,
  };
}
